import React, { useEffect, useState } from 'react';
import { XIcon, SearchIcon, MapPinIcon, UserIcon, ActivityIcon, AlertTriangleIcon, WifiIcon, ZapIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useMavisData } from '../hooks/useMavisData';
interface CheckMeterModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function CheckMeterModal({
  isOpen,
  onClose
}: CheckMeterModalProps) {
  const data = useMavisData();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [results, setResults] = useState<typeof data.smartMeters>([]);
  const [hasSearched, setHasSearched] = useState(false);
  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setResults([]);
      setHasSearched(false);
    }
  }, [isOpen]);
  if (!isOpen) return null;
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setResults([]);
      setHasSearched(false);
      return;
    }
    const query = searchQuery.toLowerCase();
    const filtered = data.smartMeters.filter(meter => meter.id.toLowerCase().includes(query) || meter.name.toLowerCase().includes(query) || meter.address.toLowerCase().includes(query));
    setResults(filtered);
    setHasSearched(true);
  };
  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Online':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Warning':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Offline':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Online':
        return <WifiIcon className="w-3 h-3" data-id="element-74" />;
      case 'Warning':
        return <AlertTriangleIcon className="w-3 h-3" data-id="element-75" />;
      case 'Offline':
        return <ActivityIcon className="w-3 h-3" data-id="element-76" />;
      default:
        return null;
    }
  };
  return <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-id="element-77">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose} data-id="element-78" />


      {/* Modal Content */}
      <div className="relative bg-white w-full max-w-2xl shadow-2xl rounded-none border border-gray-200 flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200" data-id="element-79">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50" data-id="element-80">
          <div data-id="element-81">
            <h2 className="text-xl font-bold text-gray-900" data-id="element-82">Check Meter</h2>
            <p className="text-sm text-gray-500 mt-1" data-id="element-83">
              Search by meter number, customer name, or address
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-none transition-colors" data-id="element-84">

            <XIcon className="w-5 h-5" data-id="element-85" />
          </button>
        </div>

        {/* Search Bar */}
        <div className="p-6 border-b border-gray-100" data-id="element-86">
          <form onSubmit={handleSearch} className="relative" data-id="element-87">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-88">
              <SearchIcon className="h-5 w-5 text-gray-400" data-id="element-89" />
            </div>
            <input type="text" className="block w-full pl-10 pr-24 py-3 border border-gray-300 rounded-none leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-mavis-teal focus:border-mavis-teal sm:text-sm transition-colors font-mono" placeholder="e.g. 04-1001-2002-30" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} autoFocus data-id="element-90" />

            <div className="absolute inset-y-0 right-0 flex items-center pr-1.5" data-id="element-91">
              <button type="submit" className="px-4 py-1.5 bg-mavis-teal text-white text-sm font-medium rounded-none hover:bg-mavis-teal/90 transition-colors" data-id="element-92">

                Search
              </button>
            </div>
          </form>
        </div>

        {/* Results Area */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50/50" data-id="element-93">
          {!hasSearched && results.length === 0 ? <div className="text-center py-12" data-id="element-94">
              <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3" data-id="element-95">
                <SearchIcon className="w-6 h-6 text-gray-400" data-id="element-96" />
              </div>
              <h3 className="text-sm font-medium text-gray-900" data-id="element-97">
                Enter a search term
              </h3>
              <p className="text-xs text-gray-500 mt-1" data-id="element-98">
                Try searching for "04-1001" or "Soweto"
              </p>
            </div> : hasSearched && results.length === 0 ? <div className="text-center py-12" data-id="element-99">
              <div className="mx-auto w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3" data-id="element-100">
                <AlertTriangleIcon className="w-6 h-6 text-red-500" data-id="element-101" />
              </div>
              <h3 className="text-sm font-medium text-gray-900" data-id="element-102">
                No meters found
              </h3>
              <p className="text-xs text-gray-500 mt-1" data-id="element-103">
                We couldn't find anything matching "{searchQuery}"
              </p>
            </div> : <div className="space-y-4" data-id="element-104">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2" data-id="element-105">
                {results.length} Result{results.length !== 1 ? 's' : ''} Found
              </p>
              {results.map(meter => <div key={meter.id} className="bg-white border border-gray-200 rounded-none p-4 shadow-sm hover:border-mavis-teal/50 transition-colors group" data-id="element-106">

                  <div className="flex justify-between items-start mb-3" data-id="element-107">
                    <div data-id="element-108">
                      <h4 className="font-mono font-bold text-lg text-gray-900" data-id="element-109">
                        {meter.id}
                      </h4>
                      <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-1" data-id="element-110">
                        <ActivityIcon className="w-3.5 h-3.5" data-id="element-111" />
                        <span data-id="element-112">Last comm: {meter.lastComm}</span>
                      </div>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-none text-xs font-medium border ${getStatusBadgeClass(meter.status)}`} data-id="element-113">

                      {getStatusIcon(meter.status)}
                      {meter.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-3 border-t border-gray-100" data-id="element-114">
                    <div className="space-y-2" data-id="element-115">
                      <div className="flex items-start gap-2 text-sm" data-id="element-116">
                        <UserIcon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" data-id="element-117" />
                        <span className="font-medium text-gray-900" data-id="element-118">
                          {meter.name}
                        </span>
                      </div>
                      <div className="flex items-start gap-2 text-sm" data-id="element-119">
                        <MapPinIcon className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" data-id="element-120" />
                        <span className="text-gray-600" data-id="element-121">{meter.address}</span>
                      </div>
                    </div>

                    <div className="flex gap-4 md:justify-end" data-id="element-122">
                      <div className="bg-gray-50 px-3 py-2 border border-gray-100 rounded-none" data-id="element-123">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5 flex items-center gap-1" data-id="element-124">
                          <ZapIcon className="w-3 h-3" data-id="element-125" /> Total Reading
                        </p>
                        <p className="font-mono font-bold text-mavis-teal text-sm" data-id="element-126">
                          {meter.reading.toFixed(1)}{' '}
                          <span className="text-[10px] font-sans text-gray-500 font-normal" data-id="element-127">
                            kWh
                          </span>
                        </p>
                      </div>
                      <div className="bg-gray-50 px-3 py-2 border border-gray-100 rounded-none" data-id="element-128">
                        <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-0.5" data-id="element-129">
                          Today's Usage
                        </p>
                        <p className="font-mono font-bold text-gray-900 text-sm" data-id="element-130">
                          {meter.daily.toFixed(1)}{' '}
                          <span className="text-[10px] font-sans text-gray-500 font-normal" data-id="element-131">
                            kWh
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-gray-100 flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-132">
                    <button className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 hover:bg-gray-50 rounded-none transition-colors" data-id="element-133">
                      View History
                    </button>
                    <button onClick={() => {
                onClose();
                navigate('/map');
              }} className="px-3 py-1.5 text-xs font-medium text-white bg-mavis-teal hover:bg-mavis-teal/90 rounded-none transition-colors" data-id="element-134">

                      Vend Token
                    </button>
                  </div>
                </div>)}
            </div>}
        </div>
      </div>
    </div>;
}